// src/index.ts
export interface TableColumn {
  prop: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: number | string;
  formatter?: (row: any, column: TableColumn, cellValue: any, index: number) => string;
}

export interface PrintOptions {
  title?: string;
  showHeader?: boolean;
  showFooter?: boolean;
  pageSize?: "A4" | "A3" | "A5" | "Letter";
  orientation?: "portrait" | "landscape" | "";
  margins?: string;
  fontSize?: string;
  headerStyle?: {
    backgroundColor?: string;
    color?: string;
    fontWeight?: string;
  };
  customStyles?: string;
  watermark?: string;
  logo?: string;
  [key: string]: any;
}

export interface PrintResult {
  success: boolean;
  message?: string;
}

/**
 * 高级表格打印工具类
 */
export class TablePrintUtil {
  private options: Required<PrintOptions>;

  constructor(options: any = {}) {
    this.options = {
      title: "数据表格",
      showHeader: true,
      showFooter: true,
      margins: "1cm",
      fontSize: "12px",
      headerStyle: {
        backgroundColor: "#f5f7fa",
        color: "#333",
        fontWeight: "bold",
      },
      customStyles: "",
      watermark: "",
      logo: "",
      ...options,
    };

    // this.options = {
    //   title: "数据表格",
    //   showHeader: true,
    //   showFooter: true,
    //   pageSize: "A4",
    //   orientation: "",
    //   margins: "1cm",
    //   fontSize: "12px",
    //   headerStyle: {
    //     backgroundColor: "#f5f7fa",
    //     color: "#333",
    //     fontWeight: "bold",
    //   },
    //   customStyles: "",
    //   watermark: "",
    //   logo: "",
    //   ...options,
    // };
  }

  /**
   * 打印表格
   */
  async printTable(data: any[], columns: TableColumn[], customOptions: Partial<PrintOptions> = {}): Promise<PrintResult> {
    const finalOptions = { ...this.options, ...customOptions };

    try {
      const printContent = this.generateAdvancedHTML(data, columns, finalOptions);
      await this.executePrint(printContent, finalOptions);
      return { success: true };
    } catch (error) {
      console.error("打印失败:", error);
      return {
        success: false,
        message: error instanceof Error ? error.message : "打印失败",
      };
    }
  }

  /**
   * 预览表格
   */
  previewTable(data: any[], columns: TableColumn[], options: Partial<PrintOptions> = {}): void {
    const finalOptions = { ...this.options, ...options };
    const previewContent = this.generateAdvancedHTML(data, columns, finalOptions);

    const previewWindow = window.open("", "_blank", "width=1000,height=700");
    if (!previewWindow) {
      throw new Error("无法打开预览窗口，请检查浏览器弹窗设置");
    }

    previewWindow.document.write(previewContent.replace(this.generatePrintScript(finalOptions), "// 预览模式，不自动打印"));
    previewWindow.document.close();
  }

  /**
   * 导出为HTML文件
   */
  exportToHTML(data: any[], columns: TableColumn[], filename: string = "table-export.html", options: Partial<PrintOptions> = {}): void {
    const finalOptions = { ...this.options, ...options };
    const htmlContent = this.generateAdvancedHTML(data, columns, finalOptions);

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  private generateAdvancedHTML(data: any[], columns: TableColumn[], options: Required<PrintOptions>): string {
    const currentTime = new Date().toLocaleString("zh-CN");
    const totalPages = Math.ceil(data.length / 20);

    return `
      <!DOCTYPE html>
      <html>
      <head>
          <meta charset="utf-8">
          <title>${options.title}</title>
          <style>
              ${this.generatePrintStyles(options)}
          </style>
      </head>
      <body>
          ${options.showHeader ? this.generateHeader(options, currentTime) : ""}
          ${this.generateTableContent(data, columns, options)}
          ${options.showFooter ? this.generateFooter(data.length, totalPages) : ""}
          
          <script>
              ${this.generatePrintScript(options)}
          </script>
      </body>
      </html>
    `;
  }

  private generatePrintStyles(options: Required<PrintOptions>): string {
    // size: ${options.pageSize} ${options.orientation};
    //     size: ${options.pageSize} ;
    return `
      @media print {
          body { margin: 0; }
          .no-print { display: none !important; }
          @page {
              margin: ${options.margins};
           
             
          }
      }
      
      body {
          font-family: 'Microsoft YaHei', Arial, sans-serif;
          margin: 20px;
          color: #333;
          font-size: ${options.fontSize};
          line-height: 1.6;
          ${options.watermark ? this.generateWatermarkStyle(options.watermark) : ""}
      }
      
      .print-header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #409EFF;
          padding-bottom: 15px;
          position: relative;
      }
      
      .print-logo {
          position: absolute;
          left: 0;
          top: 0;
          max-height: 50px;
          max-width: 150px;
      }
      
      .print-title {
          font-size: 28px;
          font-weight: bold;
          margin: 0 0 10px 0;
          color: #409EFF;
      }
      
      .print-subtitle {
          margin: 5px 0;
          color: #666;
          font-size: 14px;
      }
      
      .print-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: ${options.fontSize};
      }
      
      .print-table th,
      .print-table td {
          border: 1px solid #ddd;
          padding: 10px 8px;
          text-align: left;
          word-wrap: break-word;
      }
      
      .print-table th {
          background-color: ${options.headerStyle.backgroundColor};
          color: ${options.headerStyle.color};
          font-weight: ${options.headerStyle.fontWeight};
      }
      
      .print-table tbody tr:nth-child(even) {
          background-color: #fafafa;
      }
      
      .print-footer {
          margin-top: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #666;
          border-top: 1px solid #eee;
          padding-top: 15px;
      }
      
      .text-center { text-align: center; }
      .text-right { text-align: right; }
      .text-left { text-align: left; }
      
      ${options.customStyles}
    `;
  }

  private generateWatermarkStyle(watermark: string): string {
    return `
      position: relative;
      &::before {
          content: "${watermark}";
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(-45deg);
          font-size: 48px;
          color: rgba(0, 0, 0, 0.1);
          z-index: -1;
          pointer-events: none;
      }
    `;
  }

  private generateHeader(options: Required<PrintOptions>, currentTime: string): string {
    return `
      <div class="print-header">
          ${options.logo ? `<img src="${options.logo}" alt="Logo" class="print-logo">` : ""}
          <h1 class="print-title">${options.title}</h1>
          <p class="print-subtitle">打印时间: ${currentTime}</p>
      </div>
    `;
  }

  private generateTableContent(data: any[], columns: TableColumn[], options: Required<PrintOptions>): string {
    const headerRow = columns.map((col) => `<th style="${col.width ? `width: ${col.width}` : ""}">${col.label}</th>`).join("");

    const bodyRows = data
      .map((row, index) => {
        const cells = columns
          .map((col) => {
            let value = this.getNestedValue(row, col.prop);

            if (col.formatter && typeof col.formatter === "function") {
              value = col.formatter(row, col, value, index);
            }

            const alignClass = col.align ? `text-${col.align}` : "";
            return `<td class="${alignClass}">${value ?? ""}</td>`;
          })
          .join("");

        return `<tr>${cells}</tr>`;
      })
      .join("");

    return `
      <table class="print-table">
          <thead>
              <tr>${headerRow}</tr>
          </thead>
          <tbody>
              ${bodyRows}
          </tbody>
      </table>
    `;
  }

  private generateFooter(totalCount: number, totalPages: number): string {
    return `
      <div class="print-footer">
          <div>共 ${totalCount} 条记录</div>
          <div>第 1 页，共 ${totalPages} 页</div>
      </div>
    `;
  }

  private generatePrintScript(options: Required<PrintOptions>): string {
    return `
      window.onload = function() {
          setTimeout(function() {
              window.print();
          }, 800);
      }
      
      window.onafterprint = function() {
          setTimeout(function() {
              window.close();
          }, 1000);
      }
    `;
  }

  private async executePrint(content: string, options: Required<PrintOptions>): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const printWindow = window.open("", "_blank", "width=800,height=600");

        if (!printWindow) {
          reject(new Error("无法打开打印窗口，请检查浏览器弹窗设置"));
          return;
        }

        printWindow.document.write(content);
        printWindow.document.close();
        printWindow.focus();

        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            resolve();
          }, 500);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  private getNestedValue(obj: any, path: string): any {
    return path?.split(".")?.reduce((current, prop) => current?.[prop], obj);
  }
}

/**
 * Vue 组合式函数
 */
export function useTablePrint(options: PrintOptions = {}) {
  const printUtil = new TablePrintUtil(options);

  const printTable = async (data: any[], columns: TableColumn[], customOptions: Partial<PrintOptions> = {}): Promise<PrintResult> => {
    return await printUtil.printTable(data, columns, customOptions);
  };

  const previewTable = (data: any[], columns: TableColumn[], customOptions: Partial<PrintOptions> = {}): void => {
    printUtil.previewTable(data, columns, customOptions);
  };

  const exportToHTML = (data: any[], columns: TableColumn[], filename?: string, customOptions: Partial<PrintOptions> = {}): void => {
    printUtil.exportToHTML(data, columns, filename, customOptions);
  };

  return {
    printTable,
    previewTable,
    exportToHTML,
    printUtil,
  };
}

// 默认导出
export default TablePrintUtil;
